/**
 * Presentation viewer with responsive layout:
 * - Expanded (>=768px): Thumbnails on LEFT, main slide on RIGHT
 * - Compact (<768px): Main slide on TOP, thumbnails on BOTTOM
 */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import DOMPurify from 'dompurify';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { parseHTMLToSlides, serializeSlidesToHTML } from '../parser';
import { SlideThumbnailBar } from './SlideThumbnailBar';
/**
 * Detect if HTML is a rich pre-formatted presentation that should be rendered as-is.
 */ function isRichPresentation(html) {
    if (!html) return false;
    const hasInlineStyles = /<style[\s\S]*?>[\s\S]*?<\/style>/i.test(html);
    const hasDataTheme = /data-theme=/i.test(html);
    const hasTables = /<table[\s\S]*?>/i.test(html);
    const hasDefinitionLists = /<dl[\s\S]*?>/i.test(html);
    const hasCustomClasses = /class=["'][^"']*(?:columns|definitions|workstream|title-slide|closing-slide)[^"']*["']/i.test(html);
    return hasInlineStyles || hasDataTheme || hasTables || hasDefinitionLists || hasCustomClasses;
}
/**
 * Extract sections and style tag from HTML
 */ function extractSlidesData(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const sections = doc.querySelectorAll('section');
    const styleEl = doc.querySelector('style');
    return {
        sections: Array.from(sections).map((s)=>s.outerHTML),
        styleTag: styleEl ? styleEl.outerHTML : ''
    };
}
export function PresentationEditorWithControls({ value, onChange }) {
    const [slides, setSlides] = useState([]);
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
    const [isExpanded, setIsExpanded] = useState(true);
    const containerRef = useRef(null);
    // Detect if this is a rich presentation
    const isRich = useMemo(()=>isRichPresentation(value), [
        value
    ]);
    // Extract slide sections and styles for rich presentations
    const { sections: richSections, styleTag } = useMemo(()=>isRich ? extractSlidesData(value) : {
            sections: [],
            styleTag: ''
        }, [
        value,
        isRich
    ]);
    // Responsive layout detection
    useEffect(()=>{
        const container = containerRef.current;
        if (!container) return;
        const observer = new ResizeObserver((entries)=>{
            for (const entry of entries){
                setIsExpanded(entry.contentRect.width >= 768);
            }
        });
        observer.observe(container);
        return ()=>observer.disconnect();
    }, []);
    // Parse HTML to slides
    useEffect(()=>{
        if (isRich) {
            // For rich presentations, create placeholder slides from sections
            const parser = new DOMParser();
            const doc = parser.parseFromString(value, 'text/html');
            const sections = doc.querySelectorAll('section');
            const placeholderSlides = Array.from(sections).map((section, index)=>{
                const h1 = section.querySelector('h1');
                const h2 = section.querySelector('h2');
                return {
                    id: `slide-${index}-${Date.now()}`,
                    type: 'content-text',
                    title: h1?.textContent?.trim() || h2?.textContent?.trim() || `Slide ${index + 1}`
                };
            });
            setSlides(placeholderSlides.length > 0 ? placeholderSlides : [
                {
                    id: 'slide-0',
                    type: 'title',
                    title: 'Presentation'
                }
            ]);
            return;
        }
        const parsedSlides = parseHTMLToSlides(value);
        if (parsedSlides.length > 0) {
            setSlides(parsedSlides);
        } else {
            setSlides([
                {
                    id: `slide-${Date.now()}`,
                    type: 'title',
                    title: 'New Presentation',
                    subtitle: 'Click to start creating slides'
                }
            ]);
        }
    }, [
        value,
        isRich
    ]);
    // Ensure currentSlideIndex is valid
    useEffect(()=>{
        if (currentSlideIndex >= slides.length) {
            setCurrentSlideIndex(Math.max(0, slides.length - 1));
        }
    }, [
        slides.length,
        currentSlideIndex
    ]);
    // Navigation handlers
    const goToPrevSlide = useCallback(()=>{
        setCurrentSlideIndex((prev)=>Math.max(0, prev - 1));
    }, []);
    const goToNextSlide = useCallback(()=>{
        setCurrentSlideIndex((prev)=>Math.min(slides.length - 1, prev + 1));
    }, [
        slides.length
    ]);
    const navigateToSlide = useCallback((index)=>{
        setCurrentSlideIndex(index);
    }, []);
    // Keyboard navigation
    useEffect(()=>{
        const handleKeyDown = (e)=>{
            if (e.key === 'ArrowLeft') {
                goToPrevSlide();
            } else if (e.key === 'ArrowRight') {
                goToNextSlide();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return ()=>window.removeEventListener('keydown', handleKeyDown);
    }, [
        goToPrevSlide,
        goToNextSlide
    ]);
    // Notify parent of changes (for simple presentations)
    const notifyChange = useCallback((updatedSlides)=>{
        const html = serializeSlidesToHTML(updatedSlides);
        onChange?.(html);
    }, [
        onChange
    ]);
    // Slide operations (for simple presentations only)
    const addSlide = useCallback((type = 'content-bullets')=>{
        const newSlide = {
            id: `slide-${Date.now()}`,
            type,
            title: 'New Slide',
            content: type === 'content-bullets' ? {
                bullets: [
                    {
                        point: 'Bullet point'
                    }
                ]
            } : undefined
        };
        const newSlides = [
            ...slides,
            newSlide
        ];
        setSlides(newSlides);
        setCurrentSlideIndex(newSlides.length - 1);
        notifyChange(newSlides);
    }, [
        slides,
        notifyChange
    ]);
    const deleteSlide = useCallback((index)=>{
        if (slides.length === 1) return;
        const newSlides = slides.filter((_, i)=>i !== index);
        setSlides(newSlides);
        setCurrentSlideIndex((prev)=>Math.min(prev, newSlides.length - 1));
        notifyChange(newSlides);
    }, [
        slides,
        notifyChange
    ]);
    const reorderSlides = useCallback((oldIndex, newIndex)=>{
        const newSlides = [
            ...slides
        ];
        const [movedSlide] = newSlides.splice(oldIndex, 1);
        newSlides.splice(newIndex, 0, movedSlide);
        setSlides(newSlides);
        setCurrentSlideIndex(newIndex);
        notifyChange(newSlides);
    }, [
        slides,
        notifyChange
    ]);
    // Get current slide HTML
    const currentSlideHTML = useMemo(()=>{
        if (isRich) {
            return styleTag + (richSections[currentSlideIndex] || '');
        }
        const currentSlide = slides[currentSlideIndex];
        if (!currentSlide) return '';
        return serializeSlidesToHTML([
            currentSlide
        ]);
    }, [
        isRich,
        styleTag,
        richSections,
        currentSlideIndex,
        slides
    ]);
    // Thumbnail bar props
    const thumbnailBarProps = {
        slides,
        currentSlideIndex,
        onSlideClick: navigateToSlide,
        onSlideReorder: isRich ? undefined : reorderSlides,
        onSlideDelete: isRich ? undefined : deleteSlide,
        onAddSlide: isRich ? undefined : ()=>addSlide(),
        richSections: isRich ? richSections : undefined,
        styleTag: isRich ? styleTag : undefined
    };
    return /*#__PURE__*/ _jsx("div", {
        ref: containerRef,
        className: "flex h-full bg-gray-100 dark:bg-gray-900",
        children: isExpanded ? /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(SlideThumbnailBar, {
                    ...thumbnailBarProps,
                    orientation: "vertical"
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex-1 flex flex-col min-w-0",
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: "flex-1 flex items-center justify-center p-6 overflow-auto",
                            children: /*#__PURE__*/ _jsx("div", {
                                className: "reveal bg-white rounded-lg shadow-xl w-full max-w-4xl overflow-hidden",
                                style: {
                                    aspectRatio: '16 / 10'
                                },
                                children: /*#__PURE__*/ _jsx("div", {
                                    className: "slides w-full h-full overflow-auto",
                                    // biome-ignore lint/security/noDangerouslySetInnerHtml: Content sanitized
                                    dangerouslySetInnerHTML: {
                                        __html: DOMPurify.sanitize(currentSlideHTML, {
                                            ADD_TAGS: [
                                                'section',
                                                'style'
                                            ],
                                            ADD_ATTR: [
                                                'data-theme',
                                                'class',
                                                'data-background',
                                                'data-background-color'
                                            ],
                                            FORCE_BODY: true
                                        })
                                    }
                                })
                            })
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center justify-center gap-4 py-3 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700",
                            children: [
                                /*#__PURE__*/ _jsx("button", {
                                    type: "button",
                                    onClick: goToPrevSlide,
                                    disabled: currentSlideIndex === 0,
                                    className: "p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors",
                                    "aria-label": "Previous slide",
                                    children: /*#__PURE__*/ _jsx(ChevronLeft, {
                                        className: "w-6 h-6"
                                    })
                                }),
                                /*#__PURE__*/ _jsxs("span", {
                                    className: "text-sm font-medium text-gray-600 dark:text-gray-300 min-w-[80px] text-center",
                                    children: [
                                        currentSlideIndex + 1,
                                        " / ",
                                        slides.length
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("button", {
                                    type: "button",
                                    onClick: goToNextSlide,
                                    disabled: currentSlideIndex === slides.length - 1,
                                    className: "p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors",
                                    "aria-label": "Next slide",
                                    children: /*#__PURE__*/ _jsx(ChevronRight, {
                                        className: "w-6 h-6"
                                    })
                                })
                            ]
                        })
                    ]
                })
            ]
        }) : /* Compact layout: slide TOP, thumbnails BOTTOM */ /*#__PURE__*/ _jsxs("div", {
            className: "flex flex-col w-full h-full",
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex-1 flex flex-col min-h-0",
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: "flex-1 flex items-center justify-center p-4 overflow-auto",
                            children: /*#__PURE__*/ _jsx("div", {
                                className: "reveal bg-white rounded-lg shadow-xl w-full max-w-2xl overflow-hidden",
                                style: {
                                    aspectRatio: '16 / 10'
                                },
                                children: /*#__PURE__*/ _jsx("div", {
                                    className: "slides w-full h-full overflow-hidden",
                                    style: {
                                        transform: 'scale(0.8)',
                                        transformOrigin: 'top left',
                                        width: '125%',
                                        height: '125%'
                                    },
                                    children: /*#__PURE__*/ _jsx("div", {
                                        className: "w-full h-full overflow-auto",
                                        // biome-ignore lint/security/noDangerouslySetInnerHtml: Content sanitized
                                        dangerouslySetInnerHTML: {
                                            __html: DOMPurify.sanitize(currentSlideHTML, {
                                                ADD_TAGS: [
                                                    'section',
                                                    'style'
                                                ],
                                                ADD_ATTR: [
                                                    'data-theme',
                                                    'class',
                                                    'data-background',
                                                    'data-background-color'
                                                ],
                                                FORCE_BODY: true
                                            })
                                        }
                                    })
                                })
                            })
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center justify-center gap-4 py-2 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700",
                            children: [
                                /*#__PURE__*/ _jsx("button", {
                                    type: "button",
                                    onClick: goToPrevSlide,
                                    disabled: currentSlideIndex === 0,
                                    className: "p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors",
                                    "aria-label": "Previous slide",
                                    children: /*#__PURE__*/ _jsx(ChevronLeft, {
                                        className: "w-5 h-5"
                                    })
                                }),
                                /*#__PURE__*/ _jsxs("span", {
                                    className: "text-sm font-medium text-gray-600 dark:text-gray-300 min-w-[60px] text-center",
                                    children: [
                                        currentSlideIndex + 1,
                                        " / ",
                                        slides.length
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("button", {
                                    type: "button",
                                    onClick: goToNextSlide,
                                    disabled: currentSlideIndex === slides.length - 1,
                                    className: "p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors",
                                    "aria-label": "Next slide",
                                    children: /*#__PURE__*/ _jsx(ChevronRight, {
                                        className: "w-5 h-5"
                                    })
                                })
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(SlideThumbnailBar, {
                    ...thumbnailBarProps,
                    orientation: "horizontal"
                })
            ]
        })
    });
}
export { parseHTMLToSlides } from '../parser';
export { convertToPptx, downloadPptx } from '../pptx-converter';
